import { useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";
import useChartSelections from "components/charting/hooks/useChartSelections";
import type { Variants } from "types/exercise";
import OneRepMaxChartLinesContext from "../../contexts/OneRepMaxChartLinesContext";
import OneRepMaxChartHelpers, { ProgressBubbleEnum } from "../../helpers/OneRepMaxChartHelpers";
import useOneRepMaxChartData from "../../useOneRepMaxChartData";

interface OneRepMaxChartLinesProviderProps {
  children?: React.ReactNode;
}

// Plots the lines and works out their progress once, for the chart, header, labels and tooltip rows to share.
function OneRepMaxChartLinesProvider(props: OneRepMaxChartLinesProviderProps) {
  const theme = useTheme();
  const selections = useChartSelections();
  const oneRepMaxChartData = useOneRepMaxChartData();

  const spanInDays = selections.endDate.diff(selections.startDate, "day");
  const aggregationUnit = OneRepMaxChartHelpers.getAggregationUnit(spanInDays);

  // One point per day, week or month depending on the selected span.
  const lines = useMemo(
    () => OneRepMaxChartHelpers.groupLinesByPeriod(oneRepMaxChartData, aggregationUnit),
    [aggregationUnit, oneRepMaxChartData]
  );

  const entries = useMemo(() => OneRepMaxChartHelpers.getProgressEntries(lines), [lines]);

  // Each line takes the colour of its progress state, as in Capture.
  const colors = useMemo(
    () =>
      new Map(
        entries.map((entry, index) => [
          entry.variant ?? null,
          OneRepMaxChartHelpers.getProgressStateColor(
            entry.state,
            theme.palette,
            entries.findIndex(({ state }) => state === entry.state) < index
          ),
        ])
      ),
    [entries, theme.palette]
  );

  const getLineColor = useCallback(
    (variant?: Variants | null) =>
      colors.get(variant ?? null) ??
      OneRepMaxChartHelpers.getProgressStateColor(ProgressBubbleEnum.OnTrack, theme.palette),
    [colors, theme.palette]
  );

  const value = useMemo(
    () => ({ lines, aggregationUnit, spanInDays, entries, getLineColor }),
    [lines, aggregationUnit, spanInDays, entries, getLineColor]
  );

  return <OneRepMaxChartLinesContext.Provider value={value}>{props.children}</OneRepMaxChartLinesContext.Provider>;
}

export default OneRepMaxChartLinesProvider;
